import React,{useState,useEffect, useCallback, useMemo} from 'react';
import _, { sortBy } from 'lodash';
import { Form,Upload, Button,Col, Row, Select, notification, Tooltip, Input,DatePicker, Alert} from "antd";
import { InboxOutlined,PaperClipOutlined } from '@ant-design/icons';
import { MODAL_SIZE_MEDIUM, BASE_PADDING } from "constants/Layout";
import { ToolsData } from 'views/Integrations/new/util'
import ToolsIntegrationsAPI, { CustomToolAPI } from "restAPI/ToolsIntegrationAPI";
import axios from 'axios';
import { ExtensionsMap } from 'views/Integrations/IngestionScriptFormPopup';
import BaseModal from './BaseModal';
import ModalUtil from 'utils/modalUtil';
import Vocabulary from 'components/Vocabulary';
import { DeleteIcon, HelpIcon } from "components/Icons";
import { LoadingIcon } from './Icons';
import AuthorisedContainer from "components/AuthorisedContainer";
import { PermissionSetMap } from "constants/RBACConstants";
import message from 'components/CustomMessage';
import { Environments } from 'views/Findings/Constants';
import { filterVocabularyOption } from 'utils/formUtils';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import { disableFutureDates } from 'components/ColumnFilterDateRange';
import ProjectSelectWithAddButton from 'components/ProjectSelectWithAddButton';
import ProductOrSubProductSelectWithAddButton from 'components/ProductOrSubProductSelectWithAddButton';
import { removeNonPrintableUnicodeCharacters } from 'utils/fileUtils';
import { ACButtonGroupItem } from 'ac-components/ACButtonGroup';
import UploadIcon from 'ac-components/ac-icons/UploadIcon';
import { useHasDisabledEnvironment } from 'contexts/GlobalSettingsContext';

const { Dragger } = Upload;

function getToolData(res, featureFlags) {
    let _data = _.filter(ToolsData, (item) => (item.isActive) && (!item.hidePushView) &&( !_.has(item, "featureKey") || (_.has(item, "featureKey") && featureFlags[item.featureKey]))).map(o => ({ label:o.name, value: o.name.toLowerCase() }));
    
    _.forEach(res, (toolValue, toolName) => {
        _data.push({ label: toolName, value: toolName })
    });
    
    _data = sortBy([..._data], d => d.label.toLowerCase());
    return _data;
}

export const UploadWidget = React.forwardRef((props={},ref) => {
    const [toolsListing,setToolsListing]=useState([]);
    const [product,setProduct] = useState(() => props.product);
    const [form] = Form.useForm();
    const [formData, setFormData] = useState(() => props);
    const [file,setFile] = useState('');
    const [fileList,setFileList] = useState([]);
    const [extension,setExtension] = useState(props?.tools ? [...((ExtensionsMap[props?.tools?.toLowerCase()]) || ['.csv'])] : []);
    const [isProjectToolTipSupported, setIsProjectToolTipSupported] = useState(props?.tools ? checkProjectToolTipSupport(props.tools.toLowerCase()) : false)
    const featureFlags = useFeatureFlag();
    const {hasDisabledEnvironment, envValue} = useHasDisabledEnvironment();;
    const showCSVSample = props?.tools && props.tools.startsWith('Custom-');
    const fileChange = (file) => {
        let fileList = file.fileList;
        fileList = fileList.slice(-1);
        if(!extension.includes(`.${fileList[0].name.split('.').pop()}`))
        {
            deleteDoc();
            return
        }
        fileList = fileList.map(file => {
            if (file.response) {
            file.url = file.response.url;
            }
            return file;
        });

        form.setFields([{
            name: "fileName",
            value: file.file.name
        }])
        setFileList(fileList)
        setFile(file.file.originFileObj)
      }

    const uploadProps = {
        multiple: false,
        beforeUpload: false,
        onChange:fileChange
      }

    // get custom tool data
    useEffect(() => {
        const { promise, cancel } = CustomToolAPI.getCustomTools();   
        promise
            .then(customTools => setToolsListing(getToolData(customTools, featureFlags)))
            .catch(e => console.error('Error in handleChange:', e));
        return cancel;
    }, [featureFlags]);

    const onSave = async (value) =>{
        // unique key based on time
        let { scanDate, armorcodeProjects, environment } = value;
        if(hasDisabledEnvironment){
            environment = envValue;
        }
        if(!_.isEmpty(scanDate)){
            scanDate = scanDate.set({hour:0,minute:0,second:0,millisecond:0}).format();
        }

        if(!_.isUndefined(armorcodeProjects)){
            armorcodeProjects = [`${armorcodeProjects}`]
        }
        const newData = {
            ...value,
            fileName: removeNonPrintableUnicodeCharacters(value.fileName),
            ...(scanDate && { scanDate }),
            ...(armorcodeProjects && { armorcodeProjects }),
            environment
        }
       
        const key = new Date().toTimeString();
        try{
            notification.success({
                key,
                message: 'File upload is in progress',
                icon: <LoadingIcon />,
                description: (
                    <>Please do not refresh or close the browser.</>
                ),
                duration: 0,
                closeIcon: <div />
            });
            const data = await ToolsIntegrationsAPI.postUploadUrl(newData, value.tools).promise;
            await axios({method: 'put',url: data.signedUrl,data:file, requestId: key, onUploadProgress: progressEvent => console.log(progressEvent.loaded)});
            message.success(
                <>File uploaded successfully<br />
                {!window.location.hash.startsWith("#/scans") ? <>Click <a href="#/scans">here</a> to view the scan details</> : undefined}</>, 3
            );
            if(props.onScanSuccess){
                props.onScanSuccess()
            }
        }catch(e){
            console.error(e)
            if(!axios.isCancel(e)){
                message.error("Scan upload Failed")
            }
        } finally{
            notification.close(key);
        }
    }

    const onValuesChange = useCallback((e) => {
        const fieldName = Object.keys(e)[0]
        const fieldValue = e[fieldName]
        if(fieldName === "product"){
            setProduct(fieldValue)
        }else if(fieldName === "tools"){
            setExtension([...((ExtensionsMap[fieldValue.toLowerCase()]) || ['.csv'])]);
            setIsProjectToolTipSupported(checkProjectToolTipSupport(fieldValue.toLowerCase()))          
        }
        setFormData((formData) => ({ ...formData, ...e }));
    },[])

    const modalProps = {
        width:MODAL_SIZE_MEDIUM,
        title:"Scan Upload",
        onOk: async () => {
            await form.validateFields()
            form.submit()
        },
        okText: "Upload"
    }

    const dummyRequest =  ({ file,onSuccess }) => {
        try {
            if(!extension.includes(`.${file.name.split('.').pop()}`))
                {
                    message.error(`${file.name} is of invalid format`);
                    return
                }
            else{
                setTimeout(() => {
                    onSuccess("ok");
                }, 0);
            }
        } catch (e) {
            console.log(e)
            message.error(`Failed to upload - ${file.name}`)
        }
    }

    const deleteDoc = () => {
       setFileList([])
       form.setFields([{
        name: "fileName",
        value: ""
    }])
    };

    const projectAPIParams = useMemo(() => ({ scope: (formData.subProduct && formData.environment ?  
        { productId: formData.product, subProductId: formData.subProduct, envName: formData.environment } : {}) }),
    [formData]);
    return ( 
        <BaseModal
        ref={ref}
        modalProps={modalProps}
        afterClose= {props.afterClose}>
             <Form
                layout="vertical"
                form={form}
                initialValues={props}
                onFinish={onSave}
                onValuesChange={onValuesChange}
                >
            <Row gutter={BASE_PADDING}>
                    <Col span={12}>
                        <Form.Item labelAlign="left" className="flex-column" name="product" label={<Vocabulary name="product"  />} rules={[{
                required: true,
                message:  <>Select a <Vocabulary name="product" isLowerCase={true} /> name.</>
            }]}>
                    <ProductOrSubProductSelectWithAddButton 
                        dropdownType= 'product'
                        showAddButton= {true}
                        autoFocus
                        showSearch
                        optionFilterProp="label"
                        onChange={() => {
                            form.setFieldsValue({
                                subProduct: null,
                                environment: null
                            });
                        }}
                        onCreate={(createdProduct) => {
                            form.setFieldsValue({
                                product: createdProduct.id,
                                subProduct: null,
                                environment: null
                            });
                            setProduct(createdProduct.id);
                        }}
                    />
                    </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item labelAlign="left" className="flex-column" name="subProduct" label={<Vocabulary name="subProduct"  />} rules={[{
                    required: true,
                    message: <>Select a <Vocabulary name="subProduct" isLowerCase={true}/> name.</>
                }]}>
                    <ProductOrSubProductSelectWithAddButton 
                        dropdownType= 'subProduct'
                        showAddButton= {true}
                        showSearch
                        optionFilterProp="label"
                        productId = {product}
                        disabled={_.isUndefined(product)}
                        onCreate={(createdSubProduct) => {
                            form.setFieldsValue({
                                subProduct: createdSubProduct.id,
                                environment: null
                            });
                        }}
                        onChange={() => {
                            form.setFieldsValue({
                                environment: null
                            });
                        }}
                    />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={BASE_PADDING}>
                   {!hasDisabledEnvironment && <Col span={12}>
                        <Form.Item labelAlign="left" className="flex-column" name="environment" label="Environment" rules={[{
                        required: true,
                        message: "Select an environment name."
                        }]}>
                            <Select showSearch optionFilterProp="label" options={Environments} filterOption={filterVocabularyOption} />
                        </Form.Item>
                    </Col>}
                    <Col span={12}>
                        <Form.Item labelAlign="left" className="flex-column" name="armorcodeProjects" label={<><Vocabulary name="project" /> {isProjectToolTipSupported ? <Tooltip title={<>Selecting an <Vocabulary name="project" isLowerCase={true}/> with existing findings may trigger de-duplication/<Vocabulary name="mitigation" isLowerCase={true}/> logic on existing findings. </>}>&nbsp;<HelpIcon/></Tooltip>: null}</>}>
                        <ProjectSelectWithAddButton 
                            showAddButton= {true}
                            showSearch
                            disabled={props?.projectsDisabled}
                            apiParams={projectAPIParams}
                            optionFilterProp="label"
                            onCreate={(createdProject) => {
                                form.setFieldsValue({
                                    armorcodeProjects: createdProject.id
                                });
                            }}
                    />
                        </Form.Item>
                    </Col>
                    
                </Row>
                <Row gutter={BASE_PADDING}>
                    <Col span={12}>
                            <Form.Item labelAlign="left" className="flex-column" name="tools" label="Source Tool" rules={[{
                        required: true,
                        message: "Select a scan tool."
                    }]}>
                            <Select disabled={props.tools} showSearch optionFilterProp="label" options={toolsListing} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item labelAlign="left" className="flex-column" name="scanIdentifier" label="Scan Identifier">
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={BASE_PADDING}>
                    <Col span={12}>
                        <Form.Item labelAlign="left" className="flex-column" name="scanDate" label="Tool Scan Date">
                            <DatePicker disabledDate={disableFutureDates} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                    <Form.Item labelAlign="left" className="flex-column" name="tags" label="Tags">
                        <Select optionFilterProp="label" mode="tags" />
                    </Form.Item>
                </Col>
                </Row>
                
                {showCSVSample && <Row gutter={BASE_PADDING}>
                    <Col span={24}>
                            <span className="pull-right"><a href='/downloads/template.csv' download>Download Sample csv File</a></span>
                    </Col>
                </Row>}
                <Row>
                    <Col span={24}>
                          <Alert type="warning" description={<span>Uploading findings using the upload option creates separate findings and will not trigger deduplication or <Vocabulary name="mitigation" isLowerCase={true} /> for findings uploaded through other mechanisms such as Pull or Push.</span>}/>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                    <Form.Item labelAlign="left" className="flex-column" name="fileName" label="Upload File" rules={[{
                        required: true,
                        message: "Please upload file."
                    }]}>
                        <Dragger showUploadList={false} {...uploadProps} accept={extension} fileList={fileList}  customRequest={dummyRequest} disabled={!(extension.length)}>
                            <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">Drop your files or click to upload from here.</p>
                            { extension.length ? <p className="ant-upload-text">{`Please Upload ${extension} File`}</p> : null}
                        </Dragger>
                        {
                fileList.map((file, i) => {
                    return (
                        <div key={`${file.name}_${i}`} className="p-t-xs p-b-xs d-flex align-items-center justify-content-between border-top alert">
                            <div className="text-ellipsis" title={file.name}><PaperClipOutlined />{file.name}</div>
                            <Tooltip title="Delete file" placement="top">
                                 <DeleteIcon className="clickable danger" onClick={() => deleteDoc(file)} />
                            </Tooltip>
                        </div>
                    )
                })
            }
                    </Form.Item>
                    </Col>
                </Row>
            </Form>
    </BaseModal>);
})

const UploadWidgetButton = (props) =>{

    const onShow = () =>{
        ModalUtil.show({
            content: (
                <UploadWidget {...props}/>
            ),
        })
    }

    return (<AuthorisedContainer permission={PermissionSetMap.UPLOAD_SCAN} ><Button onClick={onShow} size="small">Upload Scan</Button></AuthorisedContainer>)
}
 
export const UploadWidgetButtonV2 = (props) =>{

    const onShow = () =>{
        ModalUtil.show({
            content: (
                <UploadWidget {...props}/>
            ),
        })
    }

    return (
        <AuthorisedContainer permission={PermissionSetMap.UPLOAD_SCAN} >
            <ACButtonGroupItem onClick={onShow}>
            <UploadIcon /> Upload Scan
            </ACButtonGroupItem>
        </AuthorisedContainer>
    )
}

const checkProjectToolTipSupport = (tool) => tool.startsWith('custom-') || ['burpsuite', 'burpsuite enterprise'].indexOf(tool) !== -1


export default UploadWidgetButton;
